import axios from 'axios';
import { ref, watch } from 'vue';
import { useAxios } from '@vueuse/integrations/useAxios';

import Utils from '@/api/Utils';

import {
	toSnakeCase, toCamelCase, isRequestCanceled, extractData, logError,
} from '@/api/ApiHelpers';

import { showErrorMessage } from '@/components/common/errorMessage';

import { dayJsToString, getTodayDayJs } from '@/components/common/dateUtils';

const suppressError = () => { };

const joinErrors = errors => {
	let result = [];
	for (const value of Object.values(errors)) {
		result.push(value);
	}
	return result.join();
}

const getToken = (username, password, successHandler, errorHandler) => {
	axios.post('/token/login/', { username, password }).then(successHandler).catch(errorHandler);
};

const logout = token => {
	axios.post('/token/logout/', token);
};

const changePassword = (oldPassword, password, successHandler, errorHandler) => {
	axios.post('/users/set_password/', {
		current_password: oldPassword,
		new_password: password,
	}).then(successHandler).catch(errorHandler);
};

const changeTempPassword = (password, successHandler, errorHandler) => {
	axios.post('/profile/change_temp_password/', {
		new_password: password,
	}).then(extractData(successHandler)).catch(errorHandler);
};

const generateTempPassword = (username, successHandler, errorHandler) => {
	axios.post('/profile/generate_temp_password/', {
		username,
	}).then(extractData(successHandler)).catch(errorHandler);
};

const baseStationAdd = (data, handler) => {
	axios.post('/base_station/change/', data).then(extractData(handler)).catch(logError);
};

const serializeBaseStationAdd = (data, id) => {
	let data2 = toSnakeCase(data);
	if (id) {
		data2.id = id;
	}
	return Utils.applyFunctionToFields(data2, ['x_coord', 'y_coord', 'z_coord'], (item, field) => {
		if (item[field]) {
			item[field] = Utils.m2mm(item[field]);
		}
	});
};

const baseStationGetDetails = (id, handler) => {
	axios.post('/base_station/get_details/', { id: id }).then(extractData(handler)).catch(logError);
};

const baseStationGetSettings = handler => {
	axios.get('/base_station/get_settings/').then(extractData(handler)).catch(logError);
};

const baseStationNetInfoGet = (id, handler) => {
	axios.post('/stats/base_station/net_info/', { id: id }).then(extractData(handler)).catch(logError);
};

const baseStationsNetInfoGet = handler => {
	axios.post('/stats/base_stations/net_info/').then(extractData(handler)).catch(logError);
};

const baseStationSetReliableCoords = (id, data, handler) => {
	axios.post('/base_station/set_reliable_coords/', { id, ...data }).then(extractData(handler)).catch(logError);
};

const baseStationSetRecommendedCoords = (coordsId, handler) => {
	axios.post('/base_station/set_recommended_coords/', { coords_id: coordsId }).then(extractData(handler)).catch(logError);
};

const downloadBlobData = filename => response => {
	var content = window.URL.createObjectURL(new Blob([response.data]));
	var tempUrl = document.createElement('a');
	tempUrl.href = content;
	tempUrl.setAttribute('download', filename);
	document.body.appendChild(tempUrl);
	tempUrl.click();
};

const confBridgeDownloadJson = () => {
	axios({
		url: '/conf_bridge/download/',
		method: 'GET',
		responseType: 'blob',
	}).then(downloadBlobData(`base_stations-${dayJsToString(getTodayDayJs())}.json`)).catch(logError);
};

const confBridgeUploadJSONFinish = (data, mode, handler) => {
	if (mode === 'base_stations') {
		axios.post('/conf_bridge/upload/finish/base_stations/', { data }).then(extractData(handler)).catch(logError);
	} else if (mode === 'settings') {
		axios.post('/conf_bridge/upload/finish/settings/', { data }).then(extractData(handler)).catch(logError);
	} else {
		logError(new Error('confBridgeUploadJSONFinish: unknown mode ' + mode));
	}
};

const baseStationStrImportSource = (data, handler) => {
	axios.post('/base_station/str_import/source/', data).then(extractData(handler)).catch(logError);
};

const baseStationStrImportMountpoints = (records, secure, credentials, handler) => {
	axios.post('/base_station/str_import/mountpoints/', { records, secure, credentials }).then(extractData(handler)).catch(logError);
};

const deserializeBaseStationDetails = (data) => {
	let data2 = toCamelCase(data);
	data2 = Utils.applyFunctionToFields(data2, ['xCoord', 'yCoord', 'zCoord'], (item, field) => {
		if (item[field]) {
			item[field] = Utils.mm2m(item[field]);
		}
	});
	return Utils.applyFunctionToFields(data2, ['xStd', 'yStd', 'zStd'], (item, field) => {
		if (item[field]) {
			item[field] = Utils.mkm2mm(item[field]);
		}
	});
};

const profileGetDetails = (handler) => {
	axios.get('/profile/get_details/').then(extractData(handler)).catch(logError);
};

const profileCreate = (data, handler) => {
	axios.post('/profile/create/', data).then(extractData(handler)).catch(logError);
};

const profileGetList = (handler) => {
	axios.get('/profile/get_list/').then(extractData(handler)).catch(logError);
};

const profileUpdate = (data, handler) => {
	axios.post('/profile/update/', toSnakeCase(data)).then(extractData(handler)).catch(logError);
};

const profileGenerateCasterPassword = (handler) => {
	axios.post('/profile/generate_caster_password/', {}).then(extractData(handler)).catch(logError);
};

const vrsInfoGet = (handler) => {
	axios.get('/vrs/info/').then(extractData(handler)).catch(logError);
};

const vrsSettingsGet = (handler) => {
	axios.get('/vrs/settings/').then(extractData(handler)).catch(logError);
};

const vrsSettingsPost = (data, handler) => {
	axios.post('/vrs/settings/', toSnakeCase(data)).then(extractData(handler)).catch(logError);
};

const vrsSettingsApplyRadiusToAll = (radius, handler) => {
	axios.post('/vrs/settings/apply_radius_to_all/', { radius_vrs: radius }).then(extractData(handler)).catch(logError);
};

const trsSettingsPost = (data, handler) => {
	axios.post('/vrs/trs_settings/', data).then(extractData(handler)).catch(logError);
};

const vrsSettingsFieldPost = (field, value, handler) => {
	let data = {}
	data[field] = value;
	vrsSettingsPost(data, handler);
};

const subNetworkSettingsPost = (data, handler) => {
	axios.post('/vrs/sub_network/update/', toSnakeCase(data)).then(extractData(handler)).catch(logError);
};

const subNetworkSettingsFieldPost = (subNetwork, field, value, handler) => {
	let data = {id: subNetwork}
	data[field] = value;
	subNetworkSettingsPost(data, handler);
};

const subNetworkCreateRename = (id, data, handler) => {
	if (id === 0) {
		axios.post('/vrs/sub_network/create/', toSnakeCase(data)).then(extractData(handler)).catch(logError);
	} else {
		axios.post('/vrs/sub_network/rename/', { id, ...toSnakeCase(data) }).then(extractData(handler)).catch(logError);
	}
};

const subNetworkDelete = (subNetworkId, handler) => {
	axios.post('/vrs/sub_network/delete/', { id: subNetworkId }).then(extractData(handler)).catch(logError);
};

const subNetworkCNetClear = (subNetworkId, handler) => {
	axios.post('/vrs/sub_network/cnet/clear/', { id: subNetworkId }).then(extractData(handler)).catch(logError);
};

const subNetworkCNetDelete = (gridpointNetId, handler) => {
	axios.post('/vrs/sub_network/cnet/delete/', { id: gridpointNetId }).then(extractData(handler)).catch(logError);
};

const subNetworkCNetUpdate = (gridpointNetId, cnetNumber, week, handler) => {
	axios.post('/vrs/sub_network/cnet/update/', {
		id: gridpointNetId,
		cnet_number: cnetNumber,
		week: week,
	}).then(extractData(handler)).catch(logError);
};

const subNetworkCNetDownloadAll = () => {
	axios({
		url: '/vrs/sub_network/cnet/download_all/',
		method: 'GET',
		responseType: 'blob',
	}).then(downloadBlobData(`all-cnetworks-${dayJsToString(getTodayDayJs())}.zip`)).catch(logError);
};

const subNetworkCnetJsonUploadFinish = (subNetworkId, data, handler) => {
	axios.post('/vrs/sub_network/cnet/json/upload/finish/', { sub_network_id: subNetworkId, data }).then(extractData(handler)).catch(logError);
};

const subNetworkVrsSignalsStatsGet = (subNetworkId, handler) => {
	axios.get('/vrs/sub_network/vrs_signals_stats/', { params: { subnetwork_id: subNetworkId } }).then(extractData(handler)).catch(logError);
};

const rtcmfMountpointCreateRename = (id, label, subNetworkId, handler) => {
	const isVrs = subNetworkId > 0;
	if (id === 0) {
		if (isVrs) {
			axios.post('/settings/rtcmf_mountpoint/vrs/create/', { label, sub_network_id: subNetworkId }).then(extractData(handler)).catch(logError);
		} else {
			axios.post('/settings/rtcmf_mountpoint/near/create/', { label, sub_network_id: subNetworkId }).then(extractData(handler)).catch(logError);
		}
	} else if (isVrs) {
		axios.post('/settings/rtcmf_mountpoint/vrs/rename/', { id, label }).then(extractData(handler)).catch(logError);
	} else {
		axios.post('/settings/rtcmf_mountpoint/near/rename/', { id, label }).then(extractData(handler)).catch(logError);
	}
};

const rtcmfMountpointDelete = (rtcmfMountpointId, isVrs, handler) => {
	if (isVrs) {
		axios.post('/settings/rtcmf_mountpoint/vrs/delete/', { id: rtcmfMountpointId }).then(extractData(handler)).catch(logError);
	} else {
		axios.post('/settings/rtcmf_mountpoint/near/delete/', { id: rtcmfMountpointId }).then(extractData(handler)).catch(logError);
	}
};

const rtcmfMountpointUpdate = (rtcmfMountpointId, data, isVrs, handler) => {
	if (isVrs) {
		axios.post('/settings/rtcmf_mountpoint/vrs/update/', { id: rtcmfMountpointId, ...data }).then(extractData(handler)).catch(logError);
	} else {
		axios.post('/settings/rtcmf_mountpoint/near/update/', { id: rtcmfMountpointId, ...data }).then(extractData(handler)).catch(logError);
	}
};

const cssrMountpointVrsUpdate = (cssrMountpointId, data, handler) => {
	axios.post('/settings/cssr_mountpoint/vrs/update/cssr_messages_filters/', { id: cssrMountpointId, ...data }).then(extractData(handler)).catch(logError);
};

const cssrMountpointVrsUpdateMmiMode = (cssrMountpointId, value, handler) => {
	axios.post('/settings/cssr_mountpoint/vrs/update/mmi_mode/', { id: cssrMountpointId, value }).then(extractData(handler)).catch(logError);
};

const vrsFTPSettingsGet = (handler) => {
	axios.get('/vrs/ftp_settings/').then(extractData(handler)).catch(logError);
};

const vrsFTPSettingsPost = (data, handler) => {
	axios.post('/vrs/ftp_settings/', toSnakeCase(data)).then(extractData(handler)).catch(logError);
};

const vrsFTPSettingsApplyDefaults = (key, handler) => {
	axios.post('/vrs/ftp_settings/apply_defaults/', { key }).then(extractData(handler)).catch(logError);
};

const vrsFTPSettingsRecheck = (handler) => {
	axios.post('/vrs/ftp_settings/recheck/', {}).then(extractData(handler)).catch(logError);
};

const vrsLeapSecondsGet = (handler) => {
	axios.get('/vrs/leap_seconds/').then(extractData(handler)).catch(logError);
};

const vrsLeapSecondsPost = (data, handler) => {
	axios.post('/vrs/leap_seconds/', toSnakeCase(data)).then(extractData(handler)).catch(logError);
};

const vrsLeapSecondsUpdate = (data, handler) => {
	axios.post('/vrs/leap_seconds/update/', toSnakeCase(data)).then(extractData(handler)).catch(logError);
};

const vrsLeapSecondsDelete = (id, handler) => {
	axios.post('/vrs/leap_seconds/delete/', { id }).then(extractData(handler)).catch(logError);
};

const vrsSettingItemPost = (data, handler) => {
	axios.post('/vrs/vrs_setting_item/', toSnakeCase(data)).then(extractData(handler)).catch(logError);
};

const containerIpAddrAutoDetect = handler => {
	axios.post('/vrs/container_ip_addr_auto_detect/', {}).then(extractData(handler)).catch(logError);
};

const vrsSettingsFtpoutPasswordResetPost = handler => {
	axios.post('/vrs/settings/ftpout_password/reset/', {}).then(extractData(handler)).catch(logError);
};

const taskGet = (label, handler) => {
	axios.get('/task/', { params: { label } }).then(extractData(handler)).catch(logError);
};

const taskStatusGet = (taskId, handler) => {
	axios.get('/task/status/', { params: { id: taskId } }).then(extractData(handler)).catch(logError);
};

const taskPost = (label, inputData, handler) => {
	axios.post('/task/', {
		label,
		input_data: inputData,
	}).then(extractData(handler)).catch(logError);
};

const deserializeProfile = toCamelCase;

const getUploadData = actionUrl => ({
	actionURL: axios.defaults.baseURL + actionUrl,
	headers: {
		Authorization: axios.defaults.headers.common['Authorization'],
	},
});

const containerPower = (action, handler) => {
	axios.post('/container/power/', { action }).then(extractData(handler)).catch(logError);
};

const containerCheckStatus = (taskId, handler) => {
	let data = {};
	data['task_id'] = taskId;
	axios.post('/container/check_status/', data).then(extractData(handler)).catch(suppressError);
};

const useApi = (url, method, requestFields, defaultResult, onSuccess, onError) => {
	const { data, isLoading, error, execute } = useAxios(url, { method }, { immediate: false });
	const apiData = ref(defaultResult);
	const resetData = () => {
		apiData.value = defaultResult;
	};
	watch(data, response => {
		if (response == null) {
			resetData();
		} else {
			apiData.value = response;
			if (onSuccess != null) {
				onSuccess(apiData.value);
			}
		}
	});
	watch(error, err => {
		if (err != null) {
			if (isRequestCanceled(err)) {
				console.warn('api request canceled', {
					code: err?.code,
					name: err?.name,
					message: err?.message,
					method: err?.config?.method,
					url: err?.config?.url,
					visibility: typeof document === 'undefined' ? undefined : document.visibilityState,
				});
				return;
			}
			if (onError != null) {
				onError(err);
			} else {
				logError(err);
			}
		}
	});
	const doRequest = (dataObject) => {
		(requestFields || []).forEach(field => {
			if (!Object.prototype.hasOwnProperty.call(dataObject || {}, field)) {
				console.warn(`Field "${field}" is required. Url: ${url}`);
			}
		});
		if (method === 'GET') {
			execute({
				params: dataObject,
			});
		} else {
			execute({
				data: dataObject,
			});
		}
	};
	return { apiData, isLoading, doRequest, resetData };
};

const useApiCheckStatus = (url, method, requestFields, defaultResult, onSuccess, onError) => {
	const { apiData: data, isLoading, doRequest } = useApi(url, method, requestFields, defaultResult, null, onError);
	const apiData = ref(defaultResult);
	const resetData = () => {
		apiData.value = defaultResult;
	};
	watch(data, response => {
		if (response == null) {
			resetData();
		} else {
			if (response.status === 'ok') {
				apiData.value = response;
				if (onSuccess != null) {
					onSuccess(response);
				}
			} else {
				showErrorMessage(response.errors);
			}
		}
	});
	return { apiData, isLoading, doRequest, resetData };
};

const useGanttBreaksGet = () => useApiCheckStatus(
	'/rinex/get_gantt_breaks/', 'POST',
	['start_date', 'end_date'],
	{ breaks: [], breaksNoData: [], delaysMaxDatetime: null, foundMountpoints: [] },
);

const generateTestBreaks = (handler) => {
	axios.post('/rinex/generate_test_breaks/', {}).then(extractData(handler)).catch(logError);
};

const useDelaysGet = () => useApiCheckStatus(
	'/rinex/get_delays/', 'POST',
	['start_date', 'end_date'],
	{ delays: [], interval: 5 },
);

const generateTestDelays = (handler) => {
	axios.post('/rinex/generate_test_delays/', {}).then(extractData(handler)).catch(logError);
};

const gnssVisibilityBaseStationsGet = (subNetwork, handler, errorHandler) => {
	axios.post('/rinex/get_gnss_visibility/base_stations/', { sub_network: subNetwork }).then(extractData(handler)).catch(errorHandler);
};

const gnssVisibilityGridpointsGet = (cnetId, handler, errorHandler) => {
	axios.post('/rinex/get_gnss_visibility/gridpoints/', { cnet_id: cnetId }).then(extractData(handler)).catch(errorHandler);
};

const gnssVisibilitySignalsTableBaseStationGet = (baseStationId, handler) => {
	axios.post('/rinex/get_gnss_visibility/signals_table/', { id: baseStationId }).then(extractData(handler)).catch(logError);
};

const useGnssVisibilityBaseStationsTimeSeriesGet = () => useApiCheckStatus(
	'/rinex/get_gnss_visibility_time_series/base_stations/', 'POST',
	['start_date', 'end_date', 'mountpoints', 'gnss'],
	{ items: [] },
);

const useGnssVisibilityGridpointsTimeSeriesGet = () => useApiCheckStatus(
	'/rinex/get_gnss_visibility_time_series/gridpoints/', 'POST',
	['start_date', 'end_date', 'mountpoints', 'gnss', 'cnet_id'],
	{ items: [] },
);

const snrGet = (handler, errorHandler) => {
	axios.get('/rinex/get_snr/').then(extractData(handler)).catch(errorHandler);
};

const multipathGet = (handler, errorHandler) => {
	axios.get('/rinex/get_multipath/').then(extractData(handler)).catch(errorHandler);
};

const residualsGet = (handler, errorHandler, subNetwork) => {
	axios.get('/rinex/get_residuals/', { params: { sub_network: subNetwork } }).then(extractData(handler)).catch(errorHandler);
};

const useCasterStatsMainGet = () => useApiCheckStatus(
	'/stats/caster/main/', 'GET',
	null,
	{ rows: [] },
);

const casterStatsRoversTooFarGet = (handler) => {
	axios.get('/stats/caster/rovers_too_far/').then(extractData(handler)).catch(logError);
};

const useActiveRoverSessionsGet = () => useApiCheckStatus(
	'/rovers_history/active_sessions/', 'GET',
	null,
	{ sessions: [] },
);

const useRoverStateHistoryGet = () => useApiCheckStatus(
	'/rovers_history/state_history/', 'POST',
	['start_datetime', 'end_datetime', 'rover_session_ids'],
	{ sessions: [] },
);

const casterStatsAggregationProgressGet = (handler) => {
	axios.get('/stats/caster/aggregation_progress/').then(extractData(handler)).catch(logError);
};

const utilsPerformanceGet = (days, handler) => {
	axios.get('/utils/performance/', {
		params: { days },
	}).then(extractData(handler)).catch(logError);
};

const utilsPerformanceDetailsGet = (days, commandName, metric, handler) => {
	axios.get('/utils/performance/details/', {
		params: {
			days,
			command_name: commandName,
			metric,
		},
	}).then(extractData(handler)).catch(logError);
};

const versionHistoryGet = handler => {
	axios.get('/utils/version_history/').then(extractData(handler)).catch(logError);
};

const utilsProcessMemoryGet = (days, handler) => {
	axios.get('/utils/process_memory/', {
		params: { days },
	}).then(extractData(handler)).catch(logError);
};

const useCasterDynamicsGet = () => useApiCheckStatus(
	'/stats/caster/dynamics/', 'POST',
	['start_date', 'end_date'],
	{ items: [], userTrafficItems: [], vrsItems: [], usernames: [] },
);

const useTroposphereStatsAggregatedGet = () => useApiCheckStatus(
	'/stats/troposphere/aggregated/', 'POST',
	['start_date', 'end_date', 'mountpoints', 'sub_network_id', 'show_min', 'show_max'],
	{ statsItems: [], period: 1, funcCount: 1 },
);

const useConvergenceStatsAggregatedGet = () => useApiCheckStatus(
	'/stats/convergence/aggregated/', 'POST',
	['start_date', 'end_date', 'sub_network_id', 'show_min', 'show_max'],
	{ statsItems: [], period: 1, funcCount: 1, convergenceThresholds: {} },
);

const useConvergenceCountsStatsAggregatedGet = () => useApiCheckStatus(
	'/stats/convergence/counts/aggregated/', 'POST',
	['start_date', 'end_date', 'sub_network_id', 'gnss', 'show_min', 'show_max'],
	{ statsItems: [], period: 1, funcCount: 1 },
);

const useIonosphereStatsAggregatedGet = () => useApiCheckStatus(
	'/stats/ionosphere/aggregated/', 'POST',
	['start_date', 'end_date', 'mountpoints', 'sub_network_id', 'show_min', 'show_max'],
	{ statsItems: [], period: 1, funcCount: 1, satellites: [] },
);

const useResidualsBySatelliteStatsAggregatedGet = () => useApiCheckStatus(
	'/stats/residuals_by_satellite/aggregated/', 'POST',
	['start_date', 'end_date', 'mountpoints', 'sub_network_id', 'show_min', 'show_median', 'show_max'],
	{ statsItems: [], period: 1, funcCount: 1, satellites: [] },
);

const useResidualsByBaseStationStatsAggregatedGet = () => useApiCheckStatus(
	'/stats/residuals_by_base_station/aggregated/', 'POST',
	['start_date', 'end_date', 'mountpoints', 'sub_network_id', 'gnss', 'show_min', 'show_median', 'show_max'],
	{ statsItems: [], period: 1, funcCount: 1 },
);

const useVrsStatsAggregatedGet = () => useApiCheckStatus(
	'/stats/vrs/dynamics/aggregated/', 'POST',
	['start_date', 'end_date', 'sub_network_id'],
	{ vrsItems: [], brdcCssrItems: [], cssrMountpointName: 'CSSR', period: 1, funcCount: 1 },
);

const useSatellitesPerBaseStationStatsGet = () => useApiCheckStatus(
	'/stats/satellites_per_base_station/', 'POST',
	['end_datetime', 'period', 'sub_network_id', 'mountpoints'],
	{ statsItems: [] },
);

const useSatellitesPerGridpointCountAggregatedGet = () => useApiCheckStatus(
	'/stats/satellites_per_gridpoint/count_aggregated/', 'POST',
	['start_date', 'end_date', 'agg_function', 'sub_network_id', 'gnss', 'cnet_number'],
	{ statsItems: [], period: 5, funcCount: 1 },
);

const useSatellitesPerBaseStationCountAggregatedGet = () => useApiCheckStatus(
	'/stats/satellites_per_base_station/count_aggregated/', 'POST',
	['start_date', 'end_date', 'agg_function', 'sub_network_id', 'gnss'],
	{ statsItems: [], period: 5, funcCount: 1 },
);

const useSatellitesVisibilityAggregatedGet = () => useApiCheckStatus(
	'/stats/satellites_visibility/count_aggregated/', 'POST',
	['start_date', 'end_date', 'agg_function', 'sub_network_id', 'gnss'],
	{ statsItems: [], period: 5, funcCount: 1 },
);

const useSpoofingNearElevationGet = () => useApiCheckStatus(
	'/stats/spoofing/near_elevation/', 'POST',
	['start_date', 'end_date', 'mountpoints', 'gnss', 'bands'],
	{ items: [], period: 5 },
);

const useSpoofingGssGet = () => useApiCheckStatus(
	'/stats/spoofing/gss/', 'POST',
	['start_date', 'end_date', 'mountpoints'],
	{ items: [], period: 5 },
);

const useBrdcmStatsGet = () => useApiCheckStatus(
	'/stats/brdcm/', 'POST',
	['start_date', 'end_date'],
	{ items: [], merges: [], period: 5 },
);

const useSpoofingGssWindowGet = () => useApiCheckStatus(
	'/stats/spoofing/gss/window/', 'GET',
	null,
	{ items: [] },
);

const dashboardUserPulseStatsGet = (period, handler) => {
	axios.get('/stats/dashboard/user_pulse_stats/', { params: { period } }).then(extractData(handler)).catch(logError);
};

const dashboardUserPulseEventsListGet = (event, service, firstId, lastId, handler) => {
	axios.get('/stats/dashboard/user_pulse_events_list/', {
		params: { event, service, first_id: firstId, last_id: lastId },
	}).then(extractData(handler)).catch(logError);
};

const dashboardUserPulseProfilesListGet = (handler) => {
	axios.get('/stats/dashboard/user_pulse_profiles_list/').then(extractData(handler)).catch(logError);
};

const statsCasterEventsListGet = (startDate, endDate, handler) => {
	axios.post('/stats/caster/events/', {
		start_datetime: startDate,
		end_datetime: endDate,
	}).then(extractData(handler)).catch(logError);
};

const alertsTgGetList = handle => {
	axios.get('/alerts/tg/get_list/').then(extractData(handle)).catch(logError);
};

const alertsTgCreate = (username, handle) => {
	axios.post('/alerts/tg/create/', { telegram_username: username }).then(extractData(handle)).catch(logError);
};

const alertsTgUpdate = (data, handle) => {
	axios.post('/alerts/tg/update/', toSnakeCase(data)).then(extractData(handle)).catch(logError);
};

const alertsTgVerificationStatus = (username, handle) => {
	axios.post('/alerts/tg/verification_status/', { telegram_username: username }).then(extractData(handle)).catch(logError);
};

const alertsTgDelete = (username, handle) => {
	axios.post('/alerts/tg/delete/', { telegram_username: username }).then(extractData(handle)).catch(logError);
};

const alertsTgTest = (username, handle) => {
	axios.post('/alerts/tg/test/', { telegram_username: username }).then(extractData(handle)).catch(logError);
};

const externalApiKeyGetList = handle => {
	axios.get('/external_api_key/get_list/').then(extractData(handle)).catch(logError);
};

const externalApiKeyCreate = (name, permissions, handle) => {
	axios.post('/external_api_key/create/', { name, permissions }).then(extractData(handle)).catch(logError);
};

const externalApiKeyRevoke = (id, handle) => {
	axios.post('/external_api_key/revoke/', { id }).then(extractData(handle)).catch(logError);
};

const externalApiKeyDelete = (id, handle) => {
	axios.post('/external_api_key/delete/', { id }).then(extractData(handle)).catch(logError);
};

const externalApiKeySetEnabled = (id, isEnabled, handle) => {
	axios.post('/external_api_key/set_enabled/', toSnakeCase({ apiKeyId: id, isEnabled })).then(extractData(handle)).catch(logError);
};

const externalApiKeyUpdatePermissions = (id, permissions, handle) => {
	axios.post('/external_api_key/update_permissions/', toSnakeCase({ apiKeyId: id, permissions })).then(extractData(handle)).catch(logError);
};


const vrsExternalSourceGet = (label, handler) => {
	axios.get('/vrs/external_source/', { params: { label } }).then(extractData(handler)).catch(logError);
};

const vrsExternalSourcePost = (data, handler) => {
	axios.post('/vrs/external_source/', toSnakeCase(data)).then(extractData(handler)).catch(logError);
};

const vrsExternalSourceEnable = (label, isEnabled, handler) => {
	axios.post('/vrs/external_source/enable/', {
		label,
		enabled: isEnabled,
	}).then(extractData(handler)).catch(logError);
};

const vrsExternalSourceApplyDefaults = (label, handler) => {
	axios.post('/vrs/external_source/apply_defaults/', { label }).then(extractData(handler)).catch(logError);
};


const deleteProfile = (username, handler) => {
	axios.post('/profile/delete/', { username }).then(extractData(handler)).catch(logError);
};

const baselineAdd = (data, handler) => {
	axios.post('/baseline/add/', data).then(extractData(handler)).catch(logError);
};

const baselineEdit = (data, handler) => {
	axios.post('/baseline/edit/', data).then(extractData(handler)).catch(logError);
};

const baselineGetDetails = (id, handler) => {
	axios.post('/baseline/get_details/', { id }).then(extractData(handler)).catch(logError);
};

const deserializeBaselineDetails = data => toCamelCase(data);

const serializeBaselineAdd = (data, id) => {
	let data2 = toSnakeCase(data);
	if (id) {
		data2.id = id;
	}
	return data2;
};

const baselineGetLastGGA = (id, handler) => {
	axios.get('/baseline/last_gga/', { params: { id } }).then(extractData(handler)).catch(logError);
};

const baselineGetLastRawData = (id, handler) => {
	axios.get('/baseline/last_raw_data/', { params: { id } }).then(extractData(handler)).catch(logError);
};

const baselineGetStats = (id, quality, handler) => {
	axios.get('/baseline/stats/', { params: { id, quality } }).then(extractData(handler)).catch(logError);
};

const ftpGetList = (path, handler) => {
	axios.get('/ftp/get_list/', { params: { path } }).then(extractData(handler)).catch(logError);
};

const ftpGetFile = (filename, path, successHandler, errorHandler) => {
	axios({
		url: '/ftp/get_file/?filename=' + filename + '&path=' + path,
		method: 'GET',
		responseType: 'blob',
	}).then(response => {
		downloadBlobData(filename)(response);
		successHandler(response);
	}).catch(errorHandler);
};

const ftpDownloadSelectedTaskStart = (selectedFiles, path, handler) => {
	axios.post('/ftp/task/start/', { selected_files: selectedFiles, path }).then(extractData(handler)).catch(logError);
};

const ftpGetTasks = (handler) => {
	axios.get('/ftp/task/list/').then(extractData(handler)).catch(logError);
};

const cNetNumberListGetList = (handler) => {
	axios.get('/gridpoints/get_cnet_number_list/').then(extractData(handler)).catch(logError);
};

const cNetConstructorApplyExisting = (id, subNetworkId, cnetNumbers, cnetDates, handler) => {
	const cnet_infos = cnetNumbers && cnetDates
		? cnetNumbers.map((num, idx) => ({ cnet_number: num, cnet_date: cnetDates[idx] }))
		: null;
	axios.post('/gridpoints/apply_existing/', {
		source_id: id,
		sub_network_id: subNetworkId,
		cnet_infos: cnet_infos,
	}).then(extractData(handler)).catch(logError);
};

const cNetConstructorDelete = (id, handler) => {
	axios.post('/gridpoints/delete/', { id }).then(extractData(handler)).catch(logError);
};

const roversImitationGetDetails = (handler) => {
	axios.get('/insights/rovers_imitation/get_details/').then(extractData(handler)).catch(logError);
};

const roversImitationStart = (data, handler) => {
	axios.post('/insights/rovers_imitation/start/', toSnakeCase(data)).then(extractData(handler)).catch(logError);
};

const roversImitationStop = (handler) => {
	axios.post('/insights/rovers_imitation/stop/').then(extractData(handler)).catch(logError);
};


export default {
	getToken,
	logout,
	changePassword,
	changeTempPassword,
	generateTempPassword,
	baseStationAdd, serializeBaseStationAdd,
	baseStationGetDetails,
	deserializeBaseStationDetails,
	baseStationGetSettings,
	baseStationNetInfoGet,
	baseStationsNetInfoGet,
	baseStationSetReliableCoords,
	baseStationSetRecommendedCoords,
	confBridgeDownloadJson,
	confBridgeUploadJSONFinish,
	baseStationStrImportSource,
	baseStationStrImportMountpoints,
	profileGetDetails,
	profileCreate,
	profileGetList,
	deserializeProfile,
	profileUpdate,
	profileGenerateCasterPassword,
	vrsInfoGet,
	vrsSettingsGet,
	vrsSettingsPost,
	vrsSettingsApplyRadiusToAll,
	vrsSettingsFieldPost,
	subNetworkSettingsPost,
	subNetworkSettingsFieldPost,
	subNetworkCreateRename,
	subNetworkDelete,
	subNetworkCNetClear,
	subNetworkCNetDelete,
	subNetworkCNetUpdate,
	subNetworkCNetDownloadAll,
	subNetworkCnetJsonUploadFinish,
	subNetworkVrsSignalsStatsGet,
	rtcmfMountpointCreateRename,
	rtcmfMountpointDelete,
	rtcmfMountpointUpdate,
	cssrMountpointVrsUpdate,
	cssrMountpointVrsUpdateMmiMode,
	vrsFTPSettingsGet,
	vrsFTPSettingsPost,
	vrsFTPSettingsApplyDefaults,
	vrsFTPSettingsRecheck,
	vrsLeapSecondsGet,
	vrsLeapSecondsPost,
	vrsLeapSecondsUpdate,
	vrsLeapSecondsDelete,
	vrsSettingItemPost,
	trsSettingsPost,
	containerIpAddrAutoDetect,
	vrsSettingsFtpoutPasswordResetPost,
	vrsExternalSourceGet,
	vrsExternalSourcePost,
	vrsExternalSourceEnable,
	vrsExternalSourceApplyDefaults,
	statsCasterEventsListGet,
	taskGet,
	taskStatusGet,
	taskPost,
	joinErrors,
	logError,
	getUploadData,
	containerPower,
	containerCheckStatus,
	useGanttBreaksGet,
	generateTestBreaks,
	useDelaysGet,
	generateTestDelays,
	gnssVisibilityBaseStationsGet,
	gnssVisibilityGridpointsGet,
	gnssVisibilitySignalsTableBaseStationGet,
	useGnssVisibilityBaseStationsTimeSeriesGet,
	useGnssVisibilityGridpointsTimeSeriesGet,
	snrGet,
	multipathGet,
	residualsGet,
	useCasterStatsMainGet,
	casterStatsRoversTooFarGet,
	useActiveRoverSessionsGet,
	useRoverStateHistoryGet,
	casterStatsAggregationProgressGet,
	useCasterDynamicsGet,
	utilsPerformanceGet,
	utilsPerformanceDetailsGet,
	utilsProcessMemoryGet,
	versionHistoryGet,
	useTroposphereStatsAggregatedGet,
	useConvergenceStatsAggregatedGet,
	useConvergenceCountsStatsAggregatedGet,
	useIonosphereStatsAggregatedGet,
	useResidualsBySatelliteStatsAggregatedGet,
	useResidualsByBaseStationStatsAggregatedGet,
	useVrsStatsAggregatedGet,
	useSatellitesPerBaseStationStatsGet,
	useSatellitesPerBaseStationCountAggregatedGet,
	useSatellitesPerGridpointCountAggregatedGet,
	useSatellitesVisibilityAggregatedGet,
	useSpoofingNearElevationGet,
	useSpoofingGssGet,
	useSpoofingGssWindowGet,
	useBrdcmStatsGet,
	dashboardUserPulseStatsGet,
	dashboardUserPulseEventsListGet,
	dashboardUserPulseProfilesListGet,
	alertsTgGetList,
	alertsTgCreate,
	alertsTgUpdate,
	alertsTgVerificationStatus,
	alertsTgDelete,
	alertsTgTest,
	externalApiKeyGetList,
	externalApiKeyCreate,
	externalApiKeyRevoke,
	externalApiKeyDelete,
	externalApiKeySetEnabled,
	externalApiKeyUpdatePermissions,
	deleteProfile,
	baselineAdd,
	baselineEdit,
	baselineGetDetails,
	deserializeBaselineDetails,
	serializeBaselineAdd,
	baselineGetLastGGA,
	baselineGetLastRawData,
	baselineGetStats,
	ftpGetList,
	ftpGetFile,
	ftpDownloadSelectedTaskStart,
	ftpGetTasks,
	cNetNumberListGetList,
	cNetConstructorApplyExisting,
	cNetConstructorDelete,
	roversImitationGetDetails,
	roversImitationStart,
	roversImitationStop,
}
